/**
 * One create-trip call, one set of rules.
 *
 * Every stop — the single stop of a one-leg order or each stop of a multi-leg
 * order — goes through `createEapiTripChecked`: assert the payload carries
 * lat/lng, POST, fail on a >=400 or an id-less response, then apply the
 * stale-window correction. Callers keep their own rollback and payload
 * building; only the invariant part lives here.
 *
 * Extracted from `useOrderMutations` so the confirmation flow can share it
 * rather than growing a second copy.
 */
import {
  assertTripBodyHasLatLng,
  diffCreateResponseForCorrection,
  type EapiCreateTripBody,
} from "@/lib/eapi-trip-mapper";

export interface EapiTripClient {
  createTrip: (body: unknown) => Promise<any>;
  patchTrip: (id: string, body: unknown) => Promise<any>;
}

/**
 * Post-createTrip stale-window correction. EAPI is idempotent on (org, name)
 * and can return a pre-existing trip whose planned windows differ from what we
 * just requested (see AOP-DETM-01596 L1 — POST asked for 06/16, response came
 * back as 06/15). When that happens we immediately PATCH the returned trip with
 * the requested windows so upstream matches the local leg.
 *
 * Returns `true` when a corrective PATCH was issued and succeeded, `false` when
 * no correction was needed; throws when the corrective PATCH itself fails.
 */
export async function correctStaleCreateResponse(
  client: Pick<EapiTripClient, "patchTrip">,
  requestBody: EapiCreateTripBody,
  response: any,
  ctx: { label: string },
): Promise<boolean> {
  const tripId: string | undefined = response?.data?.id;
  if (!tripId) return false;
  const respBody = response?.data ?? response;
  const delta = diffCreateResponseForCorrection(requestBody, respBody);
  if (!delta) return false;
  console.warn(
    `[eapi stale-create] ${ctx.label} trip ${tripId} returned stale fields; issuing corrective PATCH`,
    delta,
  );
  const patchRes = await client.patchTrip(tripId, delta);
  if (patchRes?.status && patchRes.status >= 400) {
    const detail =
      typeof patchRes.data === "object"
        ? JSON.stringify(patchRes.data)
        : String(patchRes.data ?? "");
    throw new Error(
      `EAPI corrective PATCH after stale createTrip failed: ${patchRes.status} ${detail}`,
    );
  }
  return true;
}

/** Creates one external trip and returns its id. Throws on any failure. */
export async function createEapiTripChecked(
  client: EapiTripClient,
  tripBody: EapiCreateTripBody,
  ctx: {
    label: string;
    assert: Parameters<typeof assertTripBodyHasLatLng>[1];
  },
): Promise<string> {
  assertTripBodyHasLatLng(tripBody, ctx.assert);
  const res = await client.createTrip(tripBody);
  if (res?.status && res.status >= 400) {
    const detail =
      typeof res.data === "object" ? JSON.stringify(res.data) : String(res.data ?? "");
    throw new Error(`EAPI sync failed: ${res.status} ${detail}`);
  }
  const tripId: string | undefined = res?.data?.id;
  if (!tripId) {
    throw new Error("EAPI response did not include a trip ID");
  }
  await correctStaleCreateResponse(client, tripBody, res, { label: ctx.label });
  return tripId;
}
